import { useState, useEffect, useCallback } from 'react'
import {
  Card, Table, Button, Space, Modal, Form, Input, Select, InputNumber,
  DatePicker, message, Popconfirm, Tag, Divider
} from 'antd'
import { PlusOutlined, DeleteOutlined, ReloadOutlined, EditOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'

interface DailyMenuItem {
  id?: number
  category: string
  dish_name: string
  sort_order: number
  price?: number
}

interface DailyMenu {
  id: number
  date: string
  category: string
  dish_name: string
  sort_order: number
  price?: number
}

const CATEGORIES = ['大荤', '小荤', '蔬菜', '水果']

export default function DailyMenus() {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<DailyMenu[]>([])
  const [dates, setDates] = useState<string[]>([])
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [modalVisible, setModalVisible] = useState(false)
  const [editMenuItems, setEditMenuItems] = useState<DailyMenuItem[]>([])
  const [saving, setSaving] = useState(false)

  const loadDates = useCallback(async () => {
    try {
      const res = await fetch('/api/daily-menus/dates', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(r => r.json())
      if (res.code === 0) {
        setDates(res.data.dates || [])
        if (!selectedDate && res.data.latest) {
          setSelectedDate(res.data.latest)
        }
      }
    } catch (err) {
      console.error('load dates error:', err)
    }
  }, [selectedDate])

  const loadData = useCallback(async (date: string) => {
    if (!date) return
    setLoading(true)
    try {
      const res = await fetch(`/api/daily-menus?date=${date}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(r => r.json())
      if (res.code === 0) {
        setData(res.data.list || [])
      }
    } catch (err) {
      console.error('load menus error:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadDates() }, [])
  useEffect(() => { if (selectedDate) loadData(selectedDate) }, [selectedDate])

  // Open edit modal
  const openEdit = () => {
    const items = data.map(d => ({
      category: d.category,
      dish_name: d.dish_name,
      sort_order: d.sort_order,
      price: d.price || undefined
    }))
    // Group - if empty, create default entries
    if (items.length === 0) {
      CATEGORIES.forEach(cat => {
        items.push({ category: cat, dish_name: '', sort_order: items.length, price: undefined })
      })
    }
    setEditMenuItems(items)
    setModalVisible(true)
  }

  const addItem = () => {
    setEditMenuItems([...editMenuItems, {
      category: CATEGORIES[0],
      dish_name: '',
      sort_order: editMenuItems.length,
      price: undefined
    }])
  }

  const removeItem = (idx: number) => {
    setEditMenuItems(editMenuItems.filter((_, i) => i !== idx))
  }

  const updateItem = (idx: number, field: string, value: any) => {
    const updated = [...editMenuItems]
    ;(updated[idx] as any)[field] = value
    setEditMenuItems(updated)
  }

  const handleSave = async () => {
    const validItems = editMenuItems.filter(i => i.dish_name && i.dish_name.trim())
    if (validItems.length === 0) {
      message.error('请至少添加一个菜品')
      return
    }
    setSaving(true)
    try {
      const res = await fetch('/api/daily-menus', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ date: selectedDate, items: validItems })
      }).then(r => r.json())
      if (res.code === 0) {
        message.success('保存成功')
        setModalVisible(false)
        loadData(selectedDate)
        loadDates()
      } else {
        message.error(res.message || '保存失败')
      }
    } catch (err) {
      message.error('保存失败')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      const res = await fetch(`/api/daily-menus?date=${selectedDate}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(r => r.json())
      if (res.code === 0) {
        message.success('已删除')
        setData([])
        loadDates()
      }
    } catch (err) {
      message.error('删除失败')
    }
  }

  const columns: ColumnsType<DailyMenu> = [
    { title: '类型', dataIndex: 'category', key: 'category', width: 80,
      render: (v: string) => <Tag>{v}</Tag>
    },
    { title: '菜品名称', dataIndex: 'dish_name', key: 'dish_name' },
    { title: '排序', dataIndex: 'sort_order', key: 'sort_order', width: 60 },
    { title: '价格(元)', dataIndex: 'price', key: 'price', width: 80,
      render: (v: number) => v != null ? v.toFixed(2) : '-'
    },
  ]

  // Group data by category for display
  const groupedData = CATEGORIES.map(cat => ({
    category: cat,
    items: data.filter(d => d.category === cat)
  })).filter(g => g.items.length > 0)

  return (
    <div>
      <Card title="每日食谱配置">
        <Space style={{ marginBottom: 16 }}>
          <Select
            style={{ width: 200 }}
            placeholder="选择日期"
            value={selectedDate || undefined}
            onChange={setSelectedDate}
            options={dates.map(d => ({ label: d, value: d }))}
          />
          {selectedDate && (
            <Button type="primary" icon={<EditOutlined />} onClick={openEdit}>
              编辑今日食谱
            </Button>
          )}
          {selectedDate && data.length > 0 && (
            <Popconfirm title="确认删除?" onConfirm={handleDelete}>
              <Button danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          )}
          <Button icon={<ReloadOutlined />} onClick={() => loadDates()}>刷新</Button>
        </Space>

        {selectedDate && data.length > 0 && (
          <div>
            <h3 style={{ marginBottom: 12, color: '#666' }}>{selectedDate} 食谱</h3>
            {groupedData.map(group => (
              <div key={group.category} style={{ marginBottom: 16 }}>
                <h4 style={{ color: '#55266c', marginBottom: 8 }}>{group.category}</h4>
                {group.items.map((item, idx) => (
                  <Tag key={idx} style={{ fontSize: 14, padding: '4px 8px', margin: 4 }}>
                    {item.dish_name}
                    {item.price != null && <span style={{ color: '#999', marginLeft: 4 }}>¥{Number(item.price).toFixed(2)}</span>}
                  </Tag>
                ))}
              </div>
            ))}
          </div>
        )}

        {selectedDate && data.length === 0 && !loading && (
          <div style={{ color: '#999', padding: 20, textAlign: 'center' }}>
            暂无今日食谱，点击"编辑今日食谱"添加
          </div>
        )}
      </Card>

      <Modal
        title={`编辑食谱 - ${selectedDate}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        confirmLoading={saving}
        width={700}
      >
        {editMenuItems.map((item, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <Select
              style={{ width: 80 }}
              value={item.category}
              onChange={v => updateItem(idx, 'category', v)}
              options={CATEGORIES.map(c => ({ label: c, value: c }))}
            />
            <Input
              style={{ flex: 1 }}
              placeholder="菜品名称"
              value={item.dish_name}
              onChange={e => updateItem(idx, 'dish_name', e.target.value)}
            />
            <InputNumber
              style={{ width: 100 }}
              placeholder="价格(元)"
              value={item.price}
              min={0}
              step={0.5}
              onChange={v => updateItem(idx, 'price', v)}
            />
            <Button danger icon={<DeleteOutlined />} onClick={() => removeItem(idx)} />
          </div>
        ))}
        <Button type="dashed" onClick={addItem} block icon={<PlusOutlined />}>
          添加菜品
        </Button>
      </Modal>
    </div>
  )
}
